import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button, Spinner, Container } from 'react-bootstrap';
import { GetJson, PutJson } from '@awp/helpers/fetchReact';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { REST_API, STATE_STATUS } from '@awp/constants';
import { FormPhoneNumber } from '@awp/components/Common/AwpFormInputs';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import ConfirmModal from '@awp/components/confirmModal';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import { AwpContainer } from './Common/AwpContainer';

const MyProfile = () => {
  const currentUser = useSelector(selectCurrentUser);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [errors, setErrors] = useState(null);
  const [showNotiWarning, setShowNotiWarning] = useState(false);

  useEffect(() => {
    if (!currentUser?.SecurityIdentifierId) return;

    const getMyProfile = async () => {
      setLoading(true);
      const response = await GetJson(REST_API.MY_PROFILE);

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else {
        setProfile(null);
      }
      setLoading(false);
    };

    getMyProfile();
  }, [currentUser?.SecurityIdentifierId]);

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);

    const data = {
      BusinessMobilePhoneNumber: values.businessCellPhone,
    };

    const updateMyProfile = async () => {
      const response = await PutJson(REST_API.MY_PROFILE, data);

      if (response.ok) {
        toast.success('User profile has been updated');
        const data = await response.json();
        setProfile(data);
        resetForm({ values });
      } else if (response.status === 412) {
        const errors = await response.json();
        setErrors(errors);
      }
    };

    updateMyProfile().finally(() => {
      setSubmitting(false);
    });
  };

  if (!profile) return null;

  if (loading) {
    return (
      <Container>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <AwpContainer>
      <h2>My Profile</h2>
      <Formik
        validateOnMount={true}
        initialValues={{
          businessCellPhone: profile.BusinessMobilePhoneNumber,
        }}
        validationSchema={Yup.object({
          businessCellPhone: Yup.string()
            .label('Business Cellular Number')
            .test('phone-number-length', 'Business cellular number must be 10 digits', (value) => {
              if (value) {
                return value && (value.length === 10 || value.length === 0);
              } else {
                return true;
              }
            }),
        })}
        onSubmit={handleSubmit}
      >
        {({ values, isValid, dirty, isSubmitting, setFieldValue, handleSubmit }) => (
          <Form>
            <Card border="light" className="page-card">
              <Card.Header>
                <Row className="mt-3">
                  <Col sm={8}>
                    <h3>{currentUser.FullName}</h3>
                  </Col>
                  <Col sm={4}>
                    <div className="mr-3" style={{ float: 'right' }}>
                      <Button
                        type="button"
                        variant="primary"
                        disabled={!isValid || !dirty || isSubmitting}
                        onClick={() => {
                          if (!isValid || !dirty || isSubmitting) return;

                          const showWarn =
                            profile.BusinessMobilePhoneNumber.length > 0 &&
                            values.businessCellPhone.length === 0 &&
                            profile.NotificationsSubscribedCount > 0;

                          if (showWarn) {
                            setShowNotiWarning(true);
                            return;
                          }

                          handleSubmit();
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                            Saving...
                          </>
                        ) : (
                          <span>Save Changes</span>
                        )}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col sm={6} className="mb-4">
                    <FormPhoneNumber
                      label="Business Cellular Number"
                      name="businessCellPhone"
                      id="businessCellPhone"
                      setFieldValue={setFieldValue}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            <ConfirmModal
              isOpen={showNotiWarning}
              toggleModal={() => {
                setShowNotiWarning(false);
              }}
              cfmConfig={{
                title: 'Warning',
                message: 'Deleting the cellular number will stop SMS alert notifications. Do you wish to proceed?',
                cfmBtnText: 'Confirm',
                cancelBtnText: 'Cancel',
                cfmAction: async () => {
                  handleSubmit();
                  setShowNotiWarning(false);
                },
              }}
              actionState=""
            ></ConfirmModal>
          </Form>
        )}
      </Formik>
      <AwpValidationErrorModal setErrors={setErrors} errors={errors} />
    </AwpContainer>
  );
};

export default MyProfile;
